
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { AppointmentForm } from "./AppointmentForm";
import { NavigationMenuImproved } from "./NavigationMenuImproved";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Menu } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ServicesDropdown } from "./ServicesDropdown";

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  
  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch navigation menu items from Supabase including parent-child relationships
  const { data: navItems = [] } = useQuery({
    queryKey: ['navigation-menu'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('navigation_menu')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Organize items into parent-child hierarchy
  const topLevelItems = navItems.filter(item => !item.parent_id);
  const getChildItems = (parentId: string) => 
    navItems.filter(item => item.parent_id === parentId);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link 
            to="/" 
            className="hover:opacity-80 transition-opacity"
            onClick={handleLogoClick}
            aria-label="Orizis - Home"
          >
            <img 
              src="/lovable-uploads/e466fd74-54b4-4116-a350-184f20b99347.png" 
              alt="Orizis Logo" 
              className="h-10 w-auto"
            />
          </Link>
          
          <div className="flex items-center space-x-6">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">
                Home
              </Link>

              <Popover open={isServicesOpen} onOpenChange={setIsServicesOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="flex items-center text-gray-700 hover:text-gray-900 transition-colors font-medium"
                    aria-haspopup="menu"
                    aria-expanded={isServicesOpen}
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    Services
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={12}
                  className="z-50 w-[min(100vw-2rem,900px)] p-4 bg-white border border-gray-200 shadow-xl rounded-xl"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <ServicesDropdown navItems={navItems as any} onNavigate={() => setIsServicesOpen(false)} />
                </PopoverContent>
              </Popover>

              <Link to="/blog" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">
                Blog
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">
                Contact
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>

            {/* CTA Button */}
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 text-sm font-medium"
                  aria-label="Schedule a consultation"
                >
                  TALK TO US
                </Button>
              </DialogTrigger>
              <AppointmentForm />
            </Dialog>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <NavigationMenuImproved 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
    </nav>
  );
};
